import React, { ReactElement, useCallback, useMemo, useState } from 'react';

import { Box, Divider, MenuItem, Typography } from '@mui/material';

import { theme } from '@/theme';
import { renderElement } from '@/utils/react-elements';

import { StyledMenu } from './DropDownMenu.styles';
import { DropDownMenuItem } from './DropDownMenu.types';

export interface DropDownMenuProps {
    id: string;
    title?: string;
    subtitle?: string;
    items: DropDownMenuItem[];
}

export const DropDownMenu = ({
    id,
    title,
    subtitle,
    items,
    children,
}: DropDownMenuProps & { children: ReactElement }) => {
    const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);

    const isOpened = useMemo(() => !!anchorElement, [anchorElement]);

    const handleClickButton = useCallback((event: React.MouseEvent<HTMLElement>) => {
        setAnchorElement(event.currentTarget);
    }, []);

    const handleClose = useCallback(() => {
        setAnchorElement(null);
    }, []);

    //

    const menuHeader = useMemo(
        () =>
            title ? (
                <MenuItem
                    autoFocus={false}
                    disableRipple
                    disableTouchRipple
                    sx={{
                        backgroundColor: 'transparent !important',
                        cursor: 'initial !important',
                        '&:hover': { backgroundColor: 'transparent !important' },
                    }}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography
                            className={`${id}-title`}
                            variant="h4"
                            component="p"
                            sx={{ color: theme.palette.common.white }}
                        >
                            {title}
                        </Typography>
                        {subtitle && (
                            <Typography
                                className={`${id}-subtitle`}
                                variant="bodyMd"
                                component="p"
                                sx={{ color: theme.palette.common.white }}
                            >
                                {subtitle}
                            </Typography>
                        )}
                    </Box>
                </MenuItem>
            ) : null,
        [id, title, subtitle],
    );

    const menuList = useMemo(
        () =>
            items.map(({ id, label, icon, action }, index) => (
                <div key={`${id}-${index}`}>
                    <MenuItem
                        data-item={id}
                        onClick={(event) => {
                            action(id, event);
                            handleClose();
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                width: '100%',
                            }}
                        >
                            <Typography variant="bodyMd" component="p" sx={{ color: theme.palette.common.white }}>
                                {label}
                            </Typography>
                            {icon
                                ? renderElement(icon, { defs: { size: 16, color: theme.palette.common.white } })
                                : null}
                        </Box>
                    </MenuItem>
                    {index < items.length - 1 && <Divider />}
                </div>
            )),
        [items, handleClose],
    );

    return (
        <>
            <div onClick={handleClickButton}>{children}</div>
            <StyledMenu classes={{ list: `${id}-list` }} anchorEl={anchorElement} open={isOpened} onClose={handleClose}>
                {menuHeader}
                {menuHeader ? <Divider /> : null}
                {menuList}
            </StyledMenu>
        </>
    );
};
